import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BottomNav } from '../components/BottomNav';
import { AppHeader } from '../components/AppHeader';
import { apiFetch } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';

interface Props { user: AuthUser; }

interface IncidentItem {
  id: string;
  title: string;
  category: string | null;
  severity: string;
  status: string;
  occurred_at: string;
  has_image?: boolean;
  reporter_user_id: string;
  reporter_name?: string | null;
  vessel_name?: string | null;
  description?: string | null;
}

type Tab = 'report' | 'history';
type Severity = 'warning' | 'critical';

interface IncidentCategory {
  id: string;
  name: string;
  color: string;
}

const SEVERITY_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: '重大', color: '#dc2626', bg: '#fef2f2' },
  warning:  { label: '注意', color: '#d97706', bg: '#fffbeb' },
};

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  open:      { label: '未対応', color: '#64748b', bg: '#f1f5f9' },
  reviewing: { label: '対応中', color: '#d97706', bg: '#fffbeb' },
  closed:    { label: '対応済', color: '#16a34a', bg: '#f0fdf4' },
};

/** datetime-local 入力用のローカル時刻文字列 (YYYY-MM-DDTHH:mm) */
function localDatetimeValue(date: Date = new Date()): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Tokyo', hour12: false,
    });
  } catch { return iso.slice(0, 16); }
}

const inputStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc',
  padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
  boxSizing: 'border-box', color: '#0f172a',
};

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#374151' };

const SafetyReportPage: React.FC<Props> = ({ user }) => {
  const [tab, setTab] = useState<Tab>('report');
  const [scope, setScope] = useState<'all' | 'mine' | 'vessel'>('all'); // 一覧の絞り込み

  // ── フォーム状態 ──
  const [occurredAt, setOccurredAt] = useState(localDatetimeValue());
  const [category, setCategory]     = useState<string>('');
  const [severity, setSeverity]     = useState<Severity>('warning');
  const [title, setTitle]           = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation]     = useState('');
  const [image, setImage]           = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── カテゴリマスタ ──
  const [categories, setCategories] = useState<IncidentCategory[]>([]);

  // ── 履歴状態 ──
  const [history, setHistory] = useState<IncidentItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 10000 : 3000);
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: IncidentItem[] }>('/incidents');
      setHistory(res.data ?? []);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch<{ data: any[] }>('/incidents/categories');
        const list = (res.data ?? [])
          .filter((c: any) => c.is_active === 1 || c.is_active === undefined)
          .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
          .map((c: any) => ({ id: c.id, name: c.name, color: c.color }));
        setCategories(list);
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  const handlePickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
    e.target.value = ''; // 同じファイルを再選択できるようリセット
  };

  const resetForm = () => {
    setOccurredAt(localDatetimeValue());
    setCategory('');
    setSeverity('warning');
    setTitle('');
    setDescription('');
    setLocation('');
    setImage(null);
  };

  // 位置情報を取得（失敗しても送信をブロックしない）
  const getGeo = (): Promise<{ latitude?: number; longitude?: number }> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) { resolve({}); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({}),
        { timeout: 8000, maximumAge: 60000 },
      );
    });

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      showToast('タイトルと状況を入力してください', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const geo = await getGeo();
      const body: Record<string, unknown> = {
        occurred_at: new Date(occurredAt).toISOString(),
        severity,
        title: title.trim(),
        description: description.trim(),
      };
      if (category)          body.category  = category;
      if (location.trim())   body.location  = location.trim();
      // ログイン中の船舶を報告に紐づける（同じ船の乗組員が閲覧できる範囲になる）
      const vesselId = localStorage.getItem('catms_last_vessel_id');
      if (vesselId)          body.vessel_id = vesselId;
      if (image)             body.image     = image;
      if (geo.latitude  !== undefined) body.latitude  = geo.latitude;
      if (geo.longitude !== undefined) body.longitude = geo.longitude;

      await apiFetch<{ data: unknown }>('/incidents', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      showToast('報告を送信しました', 'success');
      resetForm();
      setTab('history');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '送信に失敗しました';
      showToast(`❌ ${msg}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBadge = (b: { label: string; color: string; bg: string } | undefined, fallback: string) => {
    const cfg = b ?? { label: fallback, color: '#64748b', bg: '#f1f5f9' };
    return (
      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      {/* ヘッダー */}
      <AppHeader title="安全報告">
        {/* セグメント切替 */}
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 12, padding: 4 }}>
          {([['report', '報告する'], ['history', '報告一覧']] as const).map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                background: tab === key ? 'white' : 'transparent',
                color: tab === key ? '#0057d9' : '#64748b',
                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              {lbl}
            </button>
          ))}
        </div>
      </AppHeader>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tab === 'report' ? (
          <>
            {/* 発生日時 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>発生日時</span>
              <input type="datetime-local" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} style={inputStyle} />
            </div>

            {/* カテゴリ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>カテゴリ</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {categories.map(c => {
                  const on = category === c.name;
                  const accent = c.color || '#0057d9';
                  return (
                    <button key={c.id} onClick={() => setCategory(prev => prev === c.name ? '' : c.name)}
                      style={{
                        padding: '11px 8px', borderRadius: 12, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        border: on ? `2px solid ${accent}` : '1.5px solid #e4e9f0',
                        background: on ? '#eff6ff' : 'white',
                        color: on ? accent : '#3d5166',
                      }}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 重要度 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>重要度</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {([['warning', '注意'], ['critical', '重大']] as const).map(([key, lbl]) => {
                  const b = SEVERITY_BADGE[key];
                  const on = severity === key;
                  return (
                    <button key={key} onClick={() => setSeverity(key)}
                      style={{
                        flex: 1, padding: '11px 0', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                        border: on ? `2px solid ${b.color}` : '1.5px solid #e4e9f0',
                        background: on ? b.bg : 'white',
                        color: on ? b.color : '#3d5166',
                      }}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* タイトル */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>タイトル <span style={{ color: '#dc2626' }}>*</span></span>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="例：機関室で足を滑らせた" style={inputStyle} />
            </div>

            {/* 状況 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>状況 <span style={{ color: '#dc2626' }}>*</span></span>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="何が起きたか" rows={4}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            {/* 発生場所 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>発生場所（任意）</span>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)}
                placeholder="例：機関室" style={inputStyle} />
            </div>

            {/* 写真添付 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>写真（任意）</span>
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
                onChange={handlePickImage} style={{ display: 'none' }} />
              {image ? (
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <img src={image} alt="添付写真" style={{ width: '100%', display: 'block', maxHeight: 240, objectFit: 'cover' }} />
                  <button onClick={() => setImage(null)}
                    style={{
                      position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%',
                      border: 'none', background: 'rgba(15,23,42,0.75)', color: 'white', fontSize: 16, cursor: 'pointer',
                    }}>✕</button>
                </div>
              ) : (
                <button onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 14, border: '1.5px solid #e4e9f0',
                    background: 'white', color: '#3d5166', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  写真を撮る/選ぶ
                </button>
              )}
            </div>

            {/* 送信ボタン */}
            <button onClick={handleSubmit} disabled={submitting}
              style={{
                width: '100%', padding: '15px', borderRadius: 16, border: 'none',
                background: submitting ? '#93c5fd' : 'linear-gradient(145deg,#0057d9,#0369a1)',
                color: 'white', fontSize: 15, fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer', marginTop: 4,
              }}>
              {submitting ? '送信中...' : '報告する'}
            </button>
          </>
        ) : (
          <>
            {/* 絞り込み（すべて / 自分の報告 / 同じ船） */}
            <div style={{ display: 'flex', gap: 6 }}>
              {([['all', 'すべて'], ['mine', '自分の報告'], ['vessel', '同じ船']] as const).map(([key, lbl]) => (
                <button key={key} onClick={() => setScope(key)}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    border: scope === key ? '1.5px solid #0057d9' : '1.5px solid #e4e9f0',
                    background: scope === key ? '#eff6ff' : 'white',
                    color: scope === key ? '#0057d9' : '#64748b',
                  }}>
                  {lbl}
                </button>
              ))}
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>読み込み中...</div>
            ) : (() => {
              const filtered = history.filter(item =>
                scope === 'all' ? true :
                scope === 'mine' ? item.reporter_user_id === user.user_id :
                item.reporter_user_id !== user.user_id
              );
              return filtered.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                  <span style={{ fontSize: 48 }}>📋</span>
                  <p style={{ color: '#64748b', fontSize: 15, fontWeight: 600, margin: 0 }}>
                    {scope === 'vessel' ? '同じ船の報告はまだありません' : 'まだ報告はありません'}
                  </p>
                </div>
              ) : (
                filtered.map(item => {
                  const mine = item.reporter_user_id === user.user_id;
                  return (
                    <div key={item.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', flex: 1 }}>{item.title}</div>
                        {renderBadge(STATUS_BADGE[item.status], item.status)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {renderBadge(SEVERITY_BADGE[item.severity], item.severity)}
                        {item.category && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: '#f1f5f9', color: '#475569' }}>
                            {item.category}
                          </span>
                        )}
                        {item.has_image && (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>📷</span>
                        )}
                      </div>
                      {item.description && (
                        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '8px 0 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {item.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                          background: mine ? '#eff6ff' : '#f0fdf4', color: mine ? '#0057d9' : '#16a34a',
                        }}>
                          {mine ? '自分' : `👤 ${item.reporter_name ?? '乗組員'}`}
                        </span>
                        {item.vessel_name && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#e0f2fe', color: '#0369a1' }}>
                            🚢 {item.vessel_name}
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>発生: {fmtDate(item.occurred_at)}</span>
                      </div>
                    </div>
                  );
                })
              );
            })()}
          </>
        )}
      </div>

      {/* トースト */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          maxWidth: 340, padding: '12px 20px', borderRadius: 24,
          background: toast.type === 'error' ? '#dc2626' : '#0f172a',
          color: 'white', fontSize: 13, fontWeight: 600, textAlign: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}>
          {toast.msg}
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SafetyReportPage;
